import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { stockService } from '../services/api';
import { DotLoader } from 'react-spinners';
import './EarlyBuySignals.css';

const EarlyBuySignals = ({ signalsData }) => {
  const { isAppManager } = useAuth();
  const showScore = isAppManager && isAppManager();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signalsCount, setSignalsCount] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [isTrading, setIsTrading] = useState(true);

  // Fallback: fetch via REST on first load if no socket data yet
  const fetchSignals = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    stockService.getEarlySignals(controller.signal)
      .then(res => {
        if (res && res.data) {
          setData(res.data);
          setSignalsCount(res.signals_count || 0);
          setUpdatedAt(res.updated_at || null);
          setIsTrading(res.is_trading !== false);
        }
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Error fetching early signals:', err);
          setError('Không thể tải dữ liệu');
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, []);

  // Initial load: use REST only if no socket data available
  useEffect(() => {
    if (!signalsData) {
      const cleanup = fetchSignals();
      return cleanup;
    }
  }, [fetchSignals, signalsData]);

  // Realtime update from socket
  useEffect(() => {
    if (signalsData && signalsData.data) {
      setData(signalsData.data);
      setSignalsCount(signalsData.signals_count || 0);
      setUpdatedAt(signalsData.updated_at || null);
      setIsTrading(signalsData.is_trading !== false);
      setLoading(false);
    }
  }, [signalsData]);

  let displayData = showAll ? data : data.filter(d => d.has_signal);

  // Column filters — only show rows that have value in filtered columns
  const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
  if (activeFilters.length > 0) {
    displayData = displayData.filter(d =>
      activeFilters.every(([col]) => {
        if (col === 'pb20') return d.pb20;
        if (col === 'pb50') return d.pb50;
        if (col === 'mua_som') return d.mua_som && d.mua_som.signal === 'buy';
        if (col === 'bung_no') return d.bung_no && d.bung_no.signal === 'buy';
        if (col === 'vuot_ma20') return d.vuot_ma20 && d.vuot_ma20.signal === 'buy';
        if (col === 'vuot_ma90') return d.vuot_ma90 && d.vuot_ma90.signal === 'buy';
        if (col === 'vuot_kj65') return d.vuot_kj65 && d.vuot_kj65.signal === 'buy';
        return true;
      })
    );
  }

  const toggleFilter = (col) => {
    setColumnFilters(prev => ({ ...prev, [col]: !prev[col] }));
  };

  const getChangeClass = (pct) => {
    if (pct > 0) return 'text-green';
    if (pct < 0) return 'text-red';
    return 'text-neutral';
  };

  const renderSignalCell = (signal, type) => {
    if (!signal) return <span className="signal-none">-</span>;

    if (type === 'mua_som') {
      if (signal.signal === 'buy') {
        return <span className="signal-buy">{signal.threshold}</span>;
      }
      return <span className="signal-none">-</span>;
    }

    if (type === 'bung_no') {
      if (signal.signal === 'buy') {
        return <span className="signal-buy">{signal.threshold}</span>;
      }
      return <span className="signal-none">-</span>;
    }

    if (type === 'vuot_ma20') {
      if (signal.signal === 'buy') {
        return <span className="signal-buy">{(signal.ma20 * 1.005).toFixed(2)}</span>;
      }
      return <span className="signal-none">-</span>;
    }

    if (type === 'vuot_ma90') {
      if (signal.signal === 'buy') {
        return <span className="signal-buy">{(signal.ma90 * 1.005).toFixed(2)}</span>;
      }
      return <span className="signal-none">-</span>;
    }

    if (type === 'vuot_kj65') {
      if (signal.signal === 'buy') {
        return <span className="signal-buy">{(signal.kj65 * 1.005).toFixed(2)}</span>;
      }
      return <span className="signal-none">-</span>;
    }

    return <span className="signal-none">-</span>;
  };

  const renderPbBool = (val) => {
    if (val) return <span className="signal-pb">{val}</span>;
    return <span className="signal-none">-</span>;
  };

  return (
    <div className="early-signals-container">
      {loading && (
        <div className="early-signals-loading">
          <DotLoader color="#3b82f6" size={50} />
        </div>
      )}

      {!loading && !isTrading && (
        <div className="early-signals-closed">
          Ngoài phiên giao dịch — tín hiệu chỉ hoạt động trong giờ giao dịch
        </div>
      )}

      <div className="early-signals-header">
        <span className="early-signals-count">{signalsCount} tín hiệu</span>
        <label className="early-signals-toggle">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          <span>Hiện tất cả ({data.length})</span>
        </label>
        {updatedAt && (
          <span className="early-signals-updated">
            Cập nhật: {new Date(updatedAt).toLocaleString('vi-VN')}
          </span>
        )}
      </div>

      {error && (
        <div className="early-signals-error">{error}
          <button onClick={() => { setError(null); fetchSignals(); }}>Thử lại</button>
        </div>
      )}

      <div className="early-signals-table-wrapper">
        <table className="early-signals-table">
          <thead>
            <tr>
              {showScore && <th className="col-score">Score</th>}
              <th className="col-rank">#</th>
              <th className="col-sym">Mã</th>
              <th className="col-price">Giá</th>
              <th className="col-chg">%</th>
              <th className="col-vol">Vol/MA20</th>
              <th className="col-signal">
                Mua sớm
                <button className={`th-filter-btn${columnFilters.mua_som ? ' active' : ''}`} onClick={() => toggleFilter('mua_som')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.mua_som ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
              <th className="col-signal">
                Bùng nổ
                <button className={`th-filter-btn${columnFilters.bung_no ? ' active' : ''}`} onClick={() => toggleFilter('bung_no')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.bung_no ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
              <th className="col-signal">
                Vượt MA20
                <button className={`th-filter-btn${columnFilters.vuot_ma20 ? ' active' : ''}`} onClick={() => toggleFilter('vuot_ma20')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.vuot_ma20 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
              <th className="col-signal">
                Vượt MA90
                <button className={`th-filter-btn${columnFilters.vuot_ma90 ? ' active' : ''}`} onClick={() => toggleFilter('vuot_ma90')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.vuot_ma90 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
              <th className="col-signal">
                KJ65
                <button className={`th-filter-btn${columnFilters.vuot_kj65 ? ' active' : ''}`} onClick={() => toggleFilter('vuot_kj65')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.vuot_kj65 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
              <th className="col-signal">
                PB20
                <button className={`th-filter-btn${columnFilters.pb20 ? ' active' : ''}`} onClick={() => toggleFilter('pb20')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.pb20 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
              <th className="col-signal">
                PB50
                <button className={`th-filter-btn${columnFilters.pb50 ? ' active' : ''}`} onClick={() => toggleFilter('pb50')} title="Lọc">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={columnFilters.pb50 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((s, i) => (
              <tr key={s.symbol} className={s.has_signal ? 'has-signal' : ''}>
                {showScore && (
                  <td className={`col-score ${s.score >= 4 ? 'score-high' : s.score >= 2 ? 'score-mid' : ''}`}>
                    {s.score > 0 ? s.score : '-'}
                  </td>
                )}
                <td className="col-rank">{i + 1}</td>
                <td className="col-sym">{s.symbol}</td>
                <td className="col-price">{s.price}</td>
                <td className={`col-chg ${getChangeClass(s.change_pct)}`}>
                  {s.change_pct != null ? `${s.change_pct > 0 ? '+' : ''}${s.change_pct.toFixed(2)}%` : '-'}
                </td>
                <td className="col-vol">{s.vol_ratio > 0 ? s.vol_ratio.toFixed(2) : '-'}</td>
                <td className="col-signal">{renderSignalCell(s.mua_som, 'mua_som')}</td>
                <td className="col-signal">{renderSignalCell(s.bung_no, 'bung_no')}</td>
                <td className="col-signal">{renderSignalCell(s.vuot_ma20, 'vuot_ma20')}</td>
                <td className="col-signal">{renderSignalCell(s.vuot_ma90, 'vuot_ma90')}</td>
                <td className="col-signal">{renderSignalCell(s.vuot_kj65, 'vuot_kj65')}</td>
                <td className="col-signal">{renderPbBool(s.pb20)}</td>
                <td className="col-signal">{renderPbBool(s.pb50)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EarlyBuySignals;
